import { useCallback, useEffect, useState } from 'react';
import { messageFor } from './errors';

/**
 * Loads data for a screen and loads it again whenever a dependency changes,
 * such as the practices version after a live alert. Data lives in React state
 * only, never in storage.
 */
export function useLoad<T>(load: () => Promise<T>, deps: readonly unknown[]) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const run = useCallback(load, deps);
  useEffect(() => {
    let current = true;
    run().then(
      (value) => {
        if (current) {
          setData(value);
          setError(null);
        }
      },
      (err: unknown) => {
        if (current) setError(messageFor(err));
      },
    );
    return () => {
      current = false;
    };
  }, [run, tick]);
  const reload = useCallback(() => setTick((n) => n + 1), []);
  return { data, error, reload };
}
